import { Settings } from "lucide-react";
import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { BUILT_IN_PROCESSORS } from "@/types/edgeTypes";

export interface EdgeConfigurationData {
  id: string;
  labels: {
    text: string;
    position: "start" | "middle" | "end";
    color?: string;
    configurable?: boolean;
  }[];
  processor?: string;
  condition?: string;
  multiTarget?: boolean;
  targets?: string[];
  dataFlow?: {
    dataType: string;
    schema?: Record<string, unknown>;
    sampleData?: unknown;
  };
}

export interface EdgeConfigDialogProps {
  open: boolean;
  onClose: () => void;
  edgeData: EdgeConfigurationData;
  onSave: (data: EdgeConfigurationData) => void;
}

export const EdgeConfigDialog = ({
  open,
  onClose,
  edgeData,
  onSave,
}: EdgeConfigDialogProps) => {
  const [localData, setLocalData] = useState<EdgeConfigurationData>(edgeData);
  const [newLabelText, setNewLabelText] = useState("");
  const [newLabelPosition, setNewLabelPosition] = useState<"start" | "middle" | "end">("middle");

  useEffect(() => {
    setLocalData(edgeData);
  }, [edgeData]);

  const handleSave = () => {
    onSave(localData);
    onClose();
  };

  const addLabel = () => {
    if (!newLabelText.trim()) return;
    
    setLocalData(prev => ({
      ...prev,
      labels: [
        ...prev.labels,
        {
          text: newLabelText,
          position: newLabelPosition,
          configurable: true,
        }
      ]
    }));
    
    setNewLabelText("");
  };

  const removeLabel = (index: number) => {
    setLocalData(prev => ({
      ...prev,
      labels: prev.labels.filter((_, i) => i !== index)
    }));
  };

  const updateLabel = (index: number, updates: Partial<EdgeConfigurationData['labels'][0]>) => {
    setLocalData(prev => ({
      ...prev,
      labels: prev.labels.map((label, i) => 
        i === index ? { ...label, ...updates } : label
      )
    }));
  };

  const availableProcessors = BUILT_IN_PROCESSORS;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configure Edge
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="general" className="flex-1 overflow-hidden">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="labels">Labels</TabsTrigger>
            <TabsTrigger value="processor">Logic</TabsTrigger>
            <TabsTrigger value="data">Data Flow</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-auto">
            <TabsContent value="general" className="space-y-4">
              <div className="space-y-2">
                <Label>Edge ID</Label>
                <Input
                  value={localData.id}
                  onChange={(e) => setLocalData(prev => ({ ...prev, id: e.target.value }))}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={localData.multiTarget || false}
                  onCheckedChange={(checked) => 
                    setLocalData(prev => ({ ...prev, multiTarget: checked }))
                  }
                />
                <Label>Multi-target edge</Label>
              </div>

              {localData.multiTarget && (
                <div className="space-y-2">
                  <Label>Target Count</Label>
                  <div className="text-sm text-muted-foreground">
                    {localData.targets?.length || 0} targets selected
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="labels" className="space-y-4">
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Label text"
                    value={newLabelText}
                    onChange={(e) => setNewLabelText(e.target.value)}
                    className="flex-1"
                  />
                  <Select 
                    value={newLabelPosition} 
                    onValueChange={(value: "start" | "middle" | "end") => setNewLabelPosition(value)}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="start">Start</SelectItem>
                      <SelectItem value="middle">Middle</SelectItem>
                      <SelectItem value="end">End</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={addLabel} disabled={!newLabelText.trim()}>
                    Add
                  </Button>
                </div>

                <div className="space-y-2">
                  {localData.labels.map((label, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 border rounded">
                      <Badge variant="outline">{label.position}</Badge>
                      <Input
                        value={label.text}
                        onChange={(e) => updateLabel(index, { text: e.target.value })}
                        className="flex-1"
                      />
                      <Input
                        type="color"
                        value={label.color || "#64748b"}
                        onChange={(e) => updateLabel(index, { color: e.target.value })}
                        className="w-16"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeLabel(index)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="processor" className="space-y-4">
              <div className="space-y-2">
                <Label>Processor</Label>
                <Select
                  value={localData.processor || ""}
                  onValueChange={(value) => 
                    setLocalData(prev => ({ ...prev, processor: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select processor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {availableProcessors.map(processor => (
                      <SelectItem key={processor} value={processor}>
                        {processor}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {localData.processor === "conditional" && (
                <div className="space-y-2">
                  <Label>Condition</Label>
                  <Textarea
                    placeholder="JavaScript condition (e.g., data.value > 10)"
                    value={localData.condition || ""}
                    onChange={(e) => 
                      setLocalData(prev => ({ ...prev, condition: e.target.value }))
                    }
                    rows={3}
                  />
                  <div className="text-xs text-muted-foreground">
                    Use 'data' for source data and 'state' for global state
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="data" className="space-y-4">
              <div className="space-y-2">
                <Label>Data Type</Label>
                <Input
                  placeholder="e.g., string, number, object"
                  value={localData.dataFlow?.dataType || ""}
                  onChange={(e) => 
                    setLocalData(prev => ({
                      ...prev,
                      dataFlow: { ...prev.dataFlow, dataType: e.target.value }
                    }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Schema (JSON)</Label>
                <Textarea
                  placeholder='{"field1": "string", "field2": "number"}'
                  value={JSON.stringify(localData.dataFlow?.schema || {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const schema = JSON.parse(e.target.value);
                      setLocalData(prev => ({
                        ...prev,
                        dataFlow: { ...prev.dataFlow, schema }
                      }));
                    } catch {
                      // Invalid JSON, ignore
                    }
                  }}
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label>Sample Data (JSON)</Label>
                <Textarea
                  placeholder='{"example": "data"}'
                  value={JSON.stringify(localData.dataFlow?.sampleData || {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const sampleData = JSON.parse(e.target.value);
                      setLocalData(prev => ({
                        ...prev,
                        dataFlow: { ...prev.dataFlow, sampleData }
                      }));
                    } catch {
                      // Invalid JSON, ignore
                    }
                  }}
                  rows={4}
                />
              </div>
            </TabsContent>
          </div>
        </Tabs>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Configuration
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
